import { html } from "htm/preact";
import { useEffect, useState } from "preact/hooks";
import {
  ApiError,
  checkAdminKey,
  courseApiBudget,
  importExternalCourse,
  listCourseMaps,
  listLibraryCourses,
  saveLibraryCourse,
  searchExternalCourses,
  type CourseApiBudget,
  type CourseSearchResult,
} from "../http.js";
import { loadCachedAdminKey, saveCachedAdminKey } from "../adminKey.js";
import { Icon } from "../components/Icon.js";
import { CoursePlan } from "../components/CoursePlan.js";
import { findMapForCourse, loadCourseMap } from "../mapData.js";
import type { Course, CourseMap, Hole, TeeSet } from "../../../shared/types.js";
import { TERMS } from "../copy.js";
import { CARD_HOLE_COUNTS, FULL_ROUND, HALF_ROUND } from "../../../shared/handicap.js";

function blankHoles(count = FULL_ROUND): Hole[] {
  return Array.from({ length: count }, (_, i) => ({
    number: i + 1,
    par: 4,
    strokeIndex: i + 1,
    yards: null,
  }));
}

function blankCourse(): Partial<Course> {
  return {
    name: "",
    source: "manual",
    verified: false,
    teeSets: [{ name: "Default", rating: null, slope: null, holes: blankHoles() }],
  };
}

/**
 * Resize every tee on a course to `count` holes, keeping what fits.
 *
 * Every tee shares one set of holes here — par and stroke index belong to the hole, and
 * `updateHole` writes them across all of them — so the size is a property of the course
 * and not of a tee. Going up adds blanks; going down drops the tail, which is what the
 * admin asked for by choosing the smaller card. Stroke index is renumbered rather than
 * truncated, because a 1-18 index with holes 10-18 cut off is not a 1-9 index: it would
 * be missing half its ranks and fail validation on save with a message about
 * permutations that says nothing about what actually happened.
 */
function resizeCard(course: Partial<Course>, count: number): Partial<Course> {
  return {
    ...course,
    teeSets: (course.teeSets ?? []).map((t) => {
      const kept = t.holes.slice(0, count);
      const holes = [
        ...kept,
        ...Array.from({ length: Math.max(0, count - kept.length) }, (_, i) => ({
          number: kept.length + i + 1,
          par: 4,
          strokeIndex: 0,
          yards: null,
        })),
      ];
      // Rank by whatever index the kept holes carried, so a card that was 1-18 keeps
      // the same holes hardest-first when it becomes 1-9.
      const order = [...holes]
        .map((h, i) => ({ i, si: h.strokeIndex > 0 ? h.strokeIndex : Number.MAX_SAFE_INTEGER }))
        .sort((a, b) => a.si - b.si || a.i - b.i);
      const rank = new Map(order.map((o, position) => [o.i, position + 1]));
      return {
        ...t,
        holes: holes.map((h, i) => ({ ...h, number: i + 1, strokeIndex: rank.get(i) ?? i + 1 })),
      };
    }),
  };
}

/** "Murray, KY" — whichever parts the database actually has. */
function place(r: CourseSearchResult): string {
  return [r.city, r.state ?? r.country].filter(Boolean).join(", ");
}

/**
 * The course library: read by anyone, written by the admin.
 *
 * The split is new, and it is worth saying why it is here rather than being an
 * inconvenience. Two reasons, and either would be enough on its own:
 *
 *  - **The library is global.** One course row is read by every game that plays it,
 *    now and next season. A slope typed in wrong changes how many strokes somebody
 *    gets in a game the typist has nothing to do with.
 *  - **The course database costs money by the request.** golfcourseapi.com's free tier
 *    is fifty requests a day for the whole deployment — not each. An open search box
 *    is one idle afternoon away from nobody being able to add a course until tomorrow.
 *
 * So the saved courses stay visible to everyone building a round, and searching,
 * importing, creating and editing all sit behind the admin key. The Worker enforces it;
 * this screen only decides what to show.
 */
export function CourseLibrary() {
  const [library, setLibrary] = useState<Course[]>([]);
  const [mapped, setMapped] = useState<Set<string>>(new Set());

  // The admin key, and whether the Worker has agreed to it. `checking` covers the first
  // moment of the screen's life, when a cached key is being tried: without it the
  // locked panel flashes up in front of an admin who is about to be let straight in.
  const [key, setKey] = useState(loadCachedAdminKey());
  const [unlocked, setUnlocked] = useState(false);
  const [checking, setChecking] = useState(loadCachedAdminKey() !== "");
  const [lockError, setLockError] = useState<string | null>(null);
  const [budget, setBudget] = useState<CourseApiBudget | null>(null);

  const [query, setQuery] = useState("");
  const [results, setResults] = useState<CourseSearchResult[]>([]);
  const [lastSearchCached, setLastSearchCached] = useState(false);
  const [editing, setEditing] = useState<Partial<Course> | null>(null);
  const [errors, setErrors] = useState<string[]>([]);
  // Set when the Worker says a failed lookup still has a way forward. Every upstream
  // failure sets it: the course database being unreachable — or simply out of requests
  // for the day — should never be the end of the road when typing the card in by hand
  // has always worked.
  const [offerManual, setOfferManual] = useState(false);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [busy, setBusy] = useState(false);

  function refreshLibrary() {
    listLibraryCourses()
      .then(setLibrary)
      .catch(() => {});
  }

  useEffect(refreshLibrary, []);

  // Which courses have a hand-traced map. Failure is silent and simply means no badges:
  // this is a nicety on a screen whose actual job is par and yardage, and the library
  // must not depend on the map service being up.
  useEffect(() => {
    listCourseMaps()
      .then((maps) => setMapped(new Set(maps.map((m) => m.courseId))))
      .catch(() => {});
  }, []);

  // A key cached by the admin screen or the map builder unlocks this one too — it is
  // the same key and the same person. A stale one just leaves the form showing.
  useEffect(() => {
    const cached = loadCachedAdminKey();
    if (!cached) return;
    checkAdminKey(cached)
      .then(() => unlock(cached))
      .catch(() => setUnlocked(false))
      .finally(() => setChecking(false));
  }, []);

  async function unlock(withKey: string) {
    setUnlocked(true);
    setKey(withKey);
    saveCachedAdminKey(withKey);
    // A read, not a spend: worth having up front so the first search isn't a surprise.
    courseApiBudget(withKey)
      .then(setBudget)
      .catch(() => setBudget(null));
  }

  async function submitKey(e: Event) {
    e.preventDefault();
    setBusy(true);
    setLockError(null);
    try {
      await checkAdminKey(key);
      await unlock(key);
    } catch (err) {
      setLockError((err as Error).message);
      setUnlocked(false);
    } finally {
      setBusy(false);
    }
  }

  /** A search or import that didn't come back. Records the message, whether the Worker
   * says entering the course by hand is still an option, and — if the key stopped being
   * accepted mid-session — locks the screen again rather than failing on every tap. */
  function failedLookup(err: unknown) {
    setErrors([(err as Error).message]);
    setOfferManual(err instanceof ApiError && err.body["canEnterManually"] === true);
    if (err instanceof ApiError && err.status === 401) {
      setUnlocked(false);
      setLockError("That admin key is no longer being accepted — enter it again.");
    }
  }

  async function search(e: Event) {
    e.preventDefault();
    if (!query.trim()) return;
    setBusy(true);
    // Cleared like every other action on this screen. Without it a failed search left
    // its message sitting above the results of the next, successful one.
    setErrors([]);
    setOfferManual(false);
    try {
      const { results: found, cached, budget: after } = await searchExternalCourses(key, query.trim());
      setResults(found);
      setLastSearchCached(cached);
      setBudget(after);
    } catch (err) {
      failedLookup(err);
    } finally {
      setBusy(false);
    }
  }

  async function importOne(id: string) {
    setBusy(true);
    setErrors([]);
    setWarnings([]);
    setOfferManual(false);
    try {
      const { course, warnings: w, budget: after } = await importExternalCourse(key, id);
      setEditing(course);
      setWarnings(w);
      setBudget(after);
    } catch (err) {
      failedLookup(err);
    } finally {
      setBusy(false);
    }
  }

  async function save() {
    if (!editing) return;
    setBusy(true);
    setErrors([]);
    setWarnings([]);
    try {
      const { warnings: w } = await saveLibraryCourse(key, editing);
      setWarnings(w);
      setEditing(null);
      refreshLibrary();
    } catch (err) {
      if (err instanceof ApiError) {
        setErrors((err.body.errors as string[]) ?? [err.message]);
        setWarnings((err.body.warnings as string[]) ?? []);
        if (err.status === 401) {
          setUnlocked(false);
          setLockError("That admin key is no longer being accepted — enter it again.");
        }
      } else {
        setErrors([(err as Error).message]);
      }
    } finally {
      setBusy(false);
    }
  }

  return html`
    <h1>Course Library</h1>
    <p class="field-help">
      ${`Courses are saved here permanently and shared by every ${TERMS.event} — add one once and it's available whenever you build a round.`}
    </p>

    ${checking
      ? html`<p class="muted">Checking your admin key…</p>`
      : unlocked
        ? html`<${AddCoursePanel}
            query=${query}
            onQuery=${setQuery}
            onSearch=${search}
            results=${results}
            cached=${lastSearchCached}
            budget=${budget}
            busy=${busy}
            onImport=${importOne}
            onManual=${() => setEditing(blankCourse())}
          />`
        : html`<${LockedPanel}
            keyValue=${key}
            onKey=${setKey}
            onSubmit=${submitKey}
            busy=${busy}
            error=${lockError}
          />`}

    ${errors.length > 0 &&
    html`<div class="card card-danger">
      ${errors.map((e) => html`<p key=${e} class="text-danger">${e}</p>`)}
      ${offerManual &&
      html`
        <p class="field-help">You can still add it yourself — the card is fully editable.</p>
        <button
          class="secondary"
          onClick=${() => {
            setErrors([]);
            setOfferManual(false);
            setEditing(blankCourse());
          }}
        >
          <${Icon} name="plus" size=${18} /> Enter this course manually
        </button>
      `}
    </div>`}
    ${warnings.length > 0 &&
    html`<div class="card card-warn">
      ${warnings.map((w) => html`<p key=${w} class="text-warn">${w}</p>`)}
    </div>`}

    ${editing &&
    unlocked &&
    html`<${CourseEditor} course=${editing} onChange=${setEditing} onSave=${save} busy=${busy} />`}

    <div class="panel-head mt-5">
      <h2 class="m-0">Saved courses</h2>
      <span class="badge">${library.length}</span>
    </div>
    <div class="stack">
      ${library.map((c) =>
        /* Tappable only for the admin, because tapping means editing: this used to be a
           dead end — one wrong rating meant deleting the whole course and entering it
           again — and the fix was to load it back into the same editor and save over
           itself. That is still exactly what happens, for the one person allowed to. */
        unlocked
          ? html`
              <button
                class="list-row list-row-tap"
                key=${c.id}
                onClick=${() => {
                  setEditing(c);
                  setErrors([]);
                  setWarnings([]);
                }}
              >
                <${CourseRow} course=${c} mapped=${mapped.has(c.id)} />
                <${Icon} name="right" size=${18} />
              </button>
            `
          : html`
              <div class="list-row" key=${c.id}>
                <${CourseRow} course=${c} mapped=${mapped.has(c.id)} />
              </div>
            `,
      )}
      ${library.length === 0 && html`<p class="muted">No courses saved yet.</p>`}
    </div>
  `;
}

/** The inside of a saved-course row, shared by the tappable and read-only forms so the
 * two can't drift apart. */
function CourseRow({ course, mapped }: { course: Course; mapped: boolean }) {
  return html`
    <span class="avatar">${course.name.trim().charAt(0).toUpperCase()}</span>
    <span class="list-main">
      ${course.name}
      <small class="list-sub">${course.teeSets.map((t) => t.name).join(", ") || "No tee sets"}</small>
    </span>
    ${/* Whether this course has been hand-traced in the course-map builder, and so can
         show distances to the green. Shown because the difference is otherwise invisible
         until you're standing on a tee; not actionable from here, because tracing is
         admin-only and lives on its own page. */
    mapped && html`<span class="badge">mapped</span>`}
    ${!course.verified && html`<span class="badge">unverified</span>`}
  `;
}

/**
 * What someone without the admin key sees where the search box used to be.
 *
 * It says who can do it and why rather than just refusing: whoever is reading it came
 * here to look for a course, and the honest answer to "why can't I add one?" is short
 * enough to give them.
 */
function LockedPanel({
  keyValue,
  onKey,
  onSubmit,
  busy,
  error,
}: {
  keyValue: string;
  onKey: (v: string) => void;
  onSubmit: (e: Event) => void;
  busy: boolean;
  error: string | null;
}) {
  const [showForm, setShowForm] = useState(false);
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Adding a course</h2>
        <span class="badge">admin only</span>
      </div>
      <p class="field-help mt-0">
        ${`Courses here are shared by every ${TERMS.event} on this app, so adding and editing them is done by the admin — a wrong slope or stroke index changes the strokes players receive in games that have nothing to do with whoever typed it. Course lookups also come out of a shared daily allowance from the course database, which is small enough to run out.`}
      </p>
      <p class="field-help">
        Ask whoever runs this app to add the course you need; every course in the list below is
        ready to play.
      </p>
      ${showForm
        ? html`
            <form class="stack" onSubmit=${onSubmit}>
              <label class="field">
                <span class="field-label">Admin key</span>
                <input
                  type="password"
                  value=${keyValue}
                  onInput=${(e: any) => onKey(e.currentTarget.value)}
                  placeholder="Your ADMIN_KEY passphrase"
                />
              </label>
              <button type="submit" disabled=${busy || !keyValue}>Unlock</button>
            </form>
          `
        : html`
            <button class="secondary" onClick=${() => setShowForm(true)}>
              <${Icon} name="check" size=${18} /> I'm the admin
            </button>
          `}
      ${error && html`<p class="notice notice-error">${error}</p>`}
    </div>
  `;
}

/**
 * Search, import and manual entry — the admin's half of the screen.
 *
 * The allowance line is not decoration. Fifty requests is the whole deployment's day,
 * so the number that matters is how many are left, and it is shown before the search
 * rather than after the failure. A result served from the cache says so for the same
 * reason: it means that search was free, which is worth knowing when you're deciding
 * whether to try another spelling.
 */
function AddCoursePanel({
  query,
  onQuery,
  onSearch,
  results,
  cached,
  budget,
  busy,
  onImport,
  onManual,
}: {
  query: string;
  onQuery: (v: string) => void;
  onSearch: (e: Event) => void;
  results: CourseSearchResult[];
  cached: boolean;
  budget: CourseApiBudget | null;
  busy: boolean;
  onImport: (id: string) => void;
  onManual: () => void;
}) {
  const spent = budget !== null && budget.remaining <= 0;
  return html`
    <div class="panel">
      <div class="panel-head">
        <h2>Find a course</h2>
        <span class="badge">golfcourseapi.com</span>
      </div>
      ${budget &&
      html`
        <p class=${`field-help mt-0 ${spent ? "text-warn" : ""}`}>
          ${spent
            ? `Today's ${budget.limit} course-database lookups are all spent — the allowance resets overnight. You can still enter a course by hand below.`
            : `${budget.remaining} of ${budget.limit} course-database lookups left today, shared by everyone using this app. Repeat searches and re-imports come from a day-long cache and cost nothing.`}
        </p>
      `}
      <form class="row" onSubmit=${onSearch}>
        <input
          placeholder="Search by course or club name"
          value=${query}
          onInput=${(e: any) => onQuery(e.currentTarget.value)}
        />
        <button type="submit" disabled=${busy || spent}>
          <${Icon} name="search" size=${18} /> ${busy ? "…" : "Search"}
        </button>
      </form>
      ${results.length > 0 &&
      html`
        <div class="tap-list mt-3">
          ${results.map(
            (r) => html`
              <button key=${r.id} disabled=${busy} onClick=${() => onImport(r.id)}>
                <span class="list-main">
                  ${r.name}
                  <small class="list-sub">
                    ${[place(r), r.teeCount ? `${r.teeCount} tee${r.teeCount === 1 ? "" : "s"}` : null]
                      .filter(Boolean)
                      .join(" · ")}
                  </small>
                </span>
                <${Icon} name="download" size=${18} />
              </button>
            `,
          )}
        </div>
        ${cached && html`<p class="field-help">Served from the cache — this search was free.</p>`}
      `}
      ${results.length === 0 &&
      query.trim() !== "" &&
      !busy &&
      html`<p class="field-help mt-3">
        No matches yet — search, or enter the course yourself below.
      </p>`}

      <button class="secondary mt-3" onClick=${onManual}>
        <${Icon} name="plus" size=${18} /> Enter a course manually
      </button>
    </div>
  `;
}

/** Holes with somewhere to put a number on the plan. */
function holesTraced(map: CourseMap): number {
  return map.holes.filter((h) => h.greenCenter ?? h.greenFront ?? h.greenBack).length;
}

function CourseEditor({
  course,
  onChange,
  onSave,
  busy,
}: {
  course: Partial<Course>;
  onChange: (c: Partial<Course>) => void;
  onSave: () => void;
  busy: boolean;
}) {
  const teeSets = course.teeSets?.length
    ? course.teeSets
    : [{ name: "Default", rating: null, slope: null, holes: blankHoles() }];
  const [selected, setSelected] = useState(0);
  const idx = Math.min(selected, teeSets.length - 1);
  const teeSet = teeSets[idx]!;

  function patchTeeSet(patch: Partial<TeeSet>) {
    onChange({ ...course, teeSets: teeSets.map((t, i) => (i === idx ? { ...t, ...patch } : t)) });
  }

  /** Yardage differs per tee, so it only touches the selected one. Par and stroke
   * index describe the hole itself and are shared by every tee — editing them on one
   * tee and leaving the others disagreeing would silently change scoring depending on
   * which tee a round picked. */
  function updateHole(holeIdx: number, patch: Partial<Hole>) {
    const { yards, ...shared } = patch;
    onChange({
      ...course,
      teeSets: teeSets.map((t, i) => ({
        ...t,
        holes: t.holes.map((h, j) => {
          if (j !== holeIdx) return h;
          const next = { ...h, ...shared };
          if (yards !== undefined && i === idx) next.yards = yards;
          return next;
        }),
      })),
    });
  }

  /**
   * A new tee set, carrying this course's par and stroke index with its own blank
   * yardages.
   *
   * Copied rather than blank because par and stroke index belong to the hole, not to the
   * tee — `updateHole` keeps every set in step for exactly that reason — so a set that
   * started from scratch would either duplicate that work or, worse, disagree with its
   * siblings and change scoring depending on which tee a round picked. Yardage is the
   * one genuinely per-tee field, so that's the one that starts empty.
   */
  function addTeeSet() {
    const fresh: TeeSet = {
      name: `Tee ${teeSets.length + 1}`,
      rating: null,
      slope: null,
      holes: teeSet.holes.map((h) => ({ ...h, yards: null })),
    };
    onChange({ ...course, teeSets: [...teeSets, fresh] });
    setSelected(teeSets.length);
  }

  function removeTeeSet() {
    if (teeSets.length <= 1) return;
    // A round names its tee set, so removing one leaves any round playing it without a
    // card until the organiser picks another. That's recoverable, but it shouldn't be a
    // surprise.
    const ok = confirm(
      `Remove the "${teeSet.name || `Tee ${idx + 1}`}" tee set? Any round already set to play it will need a different tee choosing.`,
    );
    if (!ok) return;
    onChange({ ...course, teeSets: teeSets.filter((_, i) => i !== idx) });
    setSelected(0);
  }

  // One number for the whole course: every tee shares these holes, so a card that was
  // 18 on one tee and 9 on another would be two courses under one name.
  const cardHoles = teeSet.holes.length;
  const totalYards = teeSet.holes.reduce((sum, h) => sum + (h.yards ?? 0), 0);
  const totalPar = teeSet.holes.reduce((sum, h) => sum + h.par, 0);

  /**
   * The traced course, if there is one.
   *
   * Fetched here rather than passed down because it is the only thing on this screen
   * that needs it, it is a good few kilobytes, and a course the player never opens
   * should never cost the download. Failure is silence: no map is the ordinary state of
   * an untraced course, and this screen's actual job is par and yardage.
   */
  const [map, setMap] = useState<CourseMap | null>(null);
  const [showPlan, setShowPlan] = useState(false);
  useEffect(() => {
    let live = true;
    setMap(null);
    if (!course.id) return undefined;
    findMapForCourse(course.id)
      .then((summary) => (summary ? loadCourseMap(summary.mapId, summary.sha) : null))
      .then((m) => {
        if (live) setMap(m);
      })
      .catch(() => {});
    return () => {
      live = false;
    };
  }, [course.id]);

  return html`
    <div class="panel">
      <h2 class="mt-0">${course.id ? "Edit course" : "Review before saving"}</h2>

      ${/* Shown collapsed, because this screen is opened to fix a yardage far more often
           than to look at the property, and an overview map is a big thing to push the
           tee sets below. Left open once opened for the life of the screen. */
      map &&
      html`
        <div class="cp-block">
          <button
            class=${`ghost cp-toggle ${showPlan ? "is-open" : ""}`}
            aria-expanded=${showPlan}
            onClick=${() => setShowPlan((v) => !v)}
          >
            <${Icon} name="down" size=${18} />
            ${showPlan ? "Hide the course" : "Show the course"}
            <small class="muted">${`${holesTraced(map)} of ${map.holes.length || FULL_ROUND} holes traced`}</small>
          </button>
          ${showPlan && html`<${CoursePlan} map=${map} />`}
        </div>
      `}
      <label class="field">
        <span class="field-label">Course name</span>
        <input
          placeholder="Course name"
          value=${course.name ?? ""}
          onInput=${(e: any) => onChange({ ...course, name: e.currentTarget.value })}
        />
      </label>

      <div class="field mt-3">
        <span class="field-label">Holes</span>
        <div class="tee-tabs">
          ${CARD_HOLE_COUNTS.map(
            (n) => html`
              <button
                key=${n}
                class=${cardHoles === n ? "" : "secondary"}
                onClick=${() => {
                  if (cardHoles === n) return;
                  // Cutting a card in half throws away nine holes of work, and there is
                  // no undo on this screen.
                  if (
                    n < cardHoles &&
                    !confirm(
                      `Make this a ${n}-hole course? Holes ${n + 1} to ${cardHoles} will be removed from every tee set.`,
                    )
                  ) {
                    return;
                  }
                  onChange(resizeCard(course, n));
                }}
              >
                ${`${n} holes`}
              </button>
            `,
          )}
        </div>
        <p class="field-help">
          ${cardHoles <= HALF_ROUND
            ? "A 9-hole course. Rounds on it play all nine, and handicap allowances are halved automatically — check the rating and slope are the 9-hole figures from the scorecard."
            : "A full 18-hole course. Rounds on it can play all 18, the front nine or the back nine."}
        </p>
      </div>

      <div class="field mt-3">
        <span class="field-label">Tee sets (${teeSets.length})</span>
        <p class="field-help">
          This course has ${teeSets.length} ${teeSets.length === 1 ? "tee set" : "tee sets"}. Pick one
          to review its yardages below — all of them are saved, and each round chooses which tee it
          plays from when you set up the tournament.
        </p>
        <div class="tee-tabs">
          ${teeSets.map(
            (t, i) => html`
              <button
                key=${`${t.name}-${i}`}
                class=${i === idx ? "" : "secondary"}
                onClick=${() => setSelected(i)}
              >
                ${t.name || `Tee ${i + 1}`}
              </button>
            `,
          )}
          <button class="ghost" onClick=${addTeeSet}>
            <${Icon} name="plus" size=${16} /> Add tee
          </button>
        </div>
      </div>

      <div class="row mt-3">
        <label class="field grow-wide">
          <span class="field-label">Tee name</span>
          <input value=${teeSet.name} onInput=${(e: any) => patchTeeSet({ name: e.currentTarget.value })} />
        </label>
        <label class="field grow">
          <span class="field-label">Rating</span>
          <input
            type="number"
            step="0.1"
            inputmode="decimal"
            value=${teeSet.rating ?? ""}
            onInput=${(e: any) => patchTeeSet({ rating: Number(e.currentTarget.value) || null })}
          />
        </label>
        <label class="field grow">
          <span class="field-label">Slope</span>
          <input
            type="number"
            inputmode="numeric"
            value=${teeSet.slope ?? ""}
            onInput=${(e: any) => patchTeeSet({ slope: Number(e.currentTarget.value) || null })}
          />
        </label>
      </div>
      <p class="field-help">
        ${`Editing "${teeSet.name || `Tee ${idx + 1}`}" — par ${totalPar}${totalYards ? `, ${totalYards.toLocaleString()} yards` : ""}. Slope drives how many strokes each handicap receives, so it's worth getting right.`}
      </p>
      ${teeSets.length > 1 &&
      html`<button class="secondary" onClick=${removeTeeSet}>
        Remove this tee set
      </button>`}

      <p class="field-help mt-3">
        ${`All ${cardHoles} holes. Par and stroke index belong to the hole and are shared by every tee set here; yardage is specific to the tee selected above.`}
      </p>
      <table class="scorecard-table mt-2">
        <thead>
          <tr>
            <th>Hole</th>
            <th>Par</th>
            <th>Stroke Idx</th>
            <th>${teeSet.name || "Tee"} yds</th>
          </tr>
        </thead>
        <tbody>
          ${teeSet.holes.map(
            (h, i) => html`
              <tr key=${h.number}>
                <td>${h.number}</td>
                <td>
                  <input type="number" value=${h.par} onInput=${(e: any) => updateHole(i, { par: Number(e.currentTarget.value) })} />
                </td>
                <td>
                  <input
                    type="number"
                    value=${h.strokeIndex}
                    onInput=${(e: any) => updateHole(i, { strokeIndex: Number(e.currentTarget.value) })}
                  />
                </td>
                <td>
                  <input
                    type="number"
                    value=${h.yards ?? ""}
                    onInput=${(e: any) => updateHole(i, { yards: Number(e.currentTarget.value) || null })}
                  />
                </td>
              </tr>
            `,
          )}
        </tbody>
      </table>

      <button class="mt-3" disabled=${busy || !course.name?.trim()} onClick=${onSave}>
        Save to library
      </button>
      ${!course.name?.trim() &&
      html`<p class="field-help">Give the course a name before saving.</p>`}
    </div>
  `;
}
